import { ArrowSquareOut, Lock } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { CustomProxyForm } from "common/components/Proxy";
import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer";
import { CHROME_EXTENSION_URL } from "common/constants";
import useProxies from "common/datahooks/useProxies";
import { trackMetaPixel } from "common/helpers/utils";
import useChromeExtension from "common/hooks/useChromeExtension";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import ExtensionLogin from "./ExtensionLogin";
import LoginMethodSelection from "./LoginMethodSelection";
import PasswordLogin from "./PasswordLogin";

export interface LinkedInLoginProps {
  onSuccess: () => void;
  account?: WorkspaceAccount;
}

export default function LinkedInLogin({
  onSuccess,
  account = null,
}: LinkedInLoginProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { suggestedCountryCode } = useProxies();
  const [selectedProxy, setSelectedProxy] = useState<string>();

  const [currentAction, setCurrentAction] = useState<
    "selectMethod" | "login" | "addProxy"
  >("selectMethod");

  const [selectedLoginMethod, setSelectedLoginMethod] = useState<
    "email" | "extension"
  >("email");

  const { isExtensionAvailable, isCheckingExtension } = useChromeExtension();

  // This is used to prevent the login method from being set to the default value
  const [hasUserSelectedMethod, setHasUserSelectedMethod] = useState(false);

  const isChrome = navigator.userAgent.includes("Chrome");

  // Set the proxy location to the suggested country if it's not set
  useEffect(() => {
    if (!selectedProxy && suggestedCountryCode) {
      setSelectedProxy(suggestedCountryCode);
    }
  }, [suggestedCountryCode]);

  useEffect(() => {
    // Check if the extension is available and the user hasn't selected a method yet
    if (!isCheckingExtension && !hasUserSelectedMethod) {
      setSelectedLoginMethod(
        !isExtensionAvailable || !isChrome ? "email" : "extension",
      );
    }
  }, [isExtensionAvailable, isCheckingExtension, hasUserSelectedMethod]);

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  if (currentAction === "selectMethod") {
    return (
      <div className="flex flex-col gap-y-4">
        <Title className="mb-4">
          {account ? "Re-connect" : "Connect"} your LinkedIn account
        </Title>
        <LoginMethodSelection
          selectedLoginMethod={selectedLoginMethod}
          setSelectedLoginMethod={(type) => {
            setHasUserSelectedMethod(true);
            setSelectedLoginMethod(type);
          }}
        />
        <div className="mb-4 flex items-center justify-center gap-x-0.5">
          <Lock weight="fill" className="size-4 text-black-700" />

          <span className="text-xs text-black-700">
            Secured with TLS 1.3 encryption
          </span>
        </div>
        {!isExtensionAvailable &&
        selectedLoginMethod === "extension" &&
        isChrome ? (
          <Button
            aria-label="Get Aimfox extension"
            rightIcon={<ArrowSquareOut />}
            onClick={() => {
              trackMetaPixel("trackCustom", "ChromeExtensionInstall");
              window.open(
                CHROME_EXTENSION_URL,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            Get Aimfox extension
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => {
              trackMetaPixel(
                "trackCustom",
                selectedLoginMethod === "email"
                  ? "ManualConnection"
                  : "ExtensionLogin",
              );
              setCurrentAction("login");
            }}
          >
            Next
          </Button>
        )}
      </div>
    );
  }
  if (currentAction === "login") {
    return selectedLoginMethod === "email" ? (
      <PasswordLogin
        account={account}
        onSuccess={onSuccess}
        setCurrentAction={setCurrentAction}
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
      />
    ) : (
      <ExtensionLogin
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
        account={account}
        setCurrentAction={setCurrentAction}
        onSuccess={onSuccess}
      />
    );
  }
  if (currentAction === "addProxy") {
    return (
      <CustomProxyForm
        onBack={() => setCurrentAction("login")}
        onAdded={setSelectedProxy}
      />
    );
  }
}
